
import { useState, useRef, useEffect, FormEvent } from 'react';

const eventTypes = [
  'Wedding', 'Corporate Event', 'Birthday Party', 'Cocktail Party',
  'Baby Shower', 'Engagement Party', 'Other',
];

export default function ContactForm() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      alert('Message must be 500 characters or less.');
      return;
    }
    setStatus('sending');
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((val, key) => body.append(key, val as string));
      const res = await fetch('https://readdy.ai/api/form/d6j8kvfrgrhbthj8mqi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus('idle'), 5000);
  };

  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl shadow-sm border border-stone-100 p-8 transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: '100ms' }}
    >
      <h2 className="text-2xl font-light text-stone-800 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Send an Enquiry
      </h2>
      <p className="text-stone-400 text-sm mb-6">We'll get back to you within 24 hours.</p>

      <form id="contact-inquiry-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">First Name *</label>
            <input
              type="text"
              name="first_name"
              required
              placeholder="Bella"
              className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
            />
          </div>
          <div>
            <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Last Name *</label>
            <input
              type="text"
              name="last_name"
              required
              placeholder="Rose"
              className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Email Address *</label>
          <input
            type="email"
            name="email"
            required
            placeholder="hello@example.com"
            className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
          />
        </div>

        <div>
          <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Phone Number</label>
          <input
            type="tel"
            name="phone"
            placeholder="04XX XXX XXX"
            className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Event Type</label>
            <select
              name="event_type"
              className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition bg-white cursor-pointer"
            >
              <option value="">Select event type</option>
              {eventTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Event Date</label>
            <input
              type="date"
              name="event_date"
              className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">
            Message * <span className="normal-case text-stone-400">(max 500 characters)</span>
          </label>
          <textarea
            name="message"
            required
            maxLength={500}
            rows={4}
            placeholder="Tell us about your event, guest count, vision, or any questions you have..."
            className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={status === 'sending'}
          className="w-full py-3.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-300 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-400/30 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'sending' ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i> Sending...
            </span>
          ) : 'Send Enquiry'}
        </button>

        {status === 'success' && (
          <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
            <i className="ri-checkbox-circle-line text-base"></i>
            Thank you! We'll be in touch soon.
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-center gap-2 text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm">
            <i className="ri-error-warning-line text-base"></i>
            Something went wrong. Please try again.
          </div>
        )}
      </form>
    </div>
  );
}
